import type { CSSProperties } from 'react'
import type { ClassificationTrend } from '../types/comercial'

export function normalizeClassificationKey(value?: string | null): string {
  return (value || '').trim().toUpperCase()
}

/** Cor CSS para texto (hex, rgb, nome); gradientes não se aplicam a `color`. */
export function resolveClassificationCssColor(color: string | null | undefined): string | undefined {
  if (!color?.trim()) return undefined
  const c = color.trim()
  if (c.includes('gradient')) return undefined
  if (c.startsWith('rgb') || c.startsWith('rgba') || c.startsWith('#')) return c
  if (/^[0-9a-fA-F]{3,8}$/.test(c)) return `#${c}`
  return c
}

export function buildClassificationColorMap(
  classifications: Array<{ name: string; color?: string | null }>
): Map<string, string | null> {
  const map = new Map<string, string | null>()
  for (const row of classifications) {
    const key = normalizeClassificationKey(row.name)
    if (!key) continue
    map.set(key, row.color ?? null)
  }
  return map
}

export function getClassificationTextStyle(
  classification: string | null | undefined,
  colorMap: Map<string, string | null>
): CSSProperties {
  const key = normalizeClassificationKey(classification)
  const color = key ? resolveClassificationCssColor(colorMap.get(key)) : undefined
  const style: CSSProperties = { fontWeight: 700 }
  if (color) style.color = color
  return style
}

function darkenCssColor(color: string, factor = 0.82): string {
  const hexMatch = color.match(/^#([0-9a-fA-F]{3,8})$/)
  if (hexMatch) {
    let hex = hexMatch[1]
    if (hex.length === 3) hex = hex.split('').map((ch) => ch + ch).join('')
    if (hex.length === 6) {
      const r = Math.round(parseInt(hex.slice(0, 2), 16) * factor)
      const g = Math.round(parseInt(hex.slice(2, 4), 16) * factor)
      const b = Math.round(parseInt(hex.slice(4, 6), 16) * factor)
      return `rgb(${r}, ${g}, ${b})`
    }
  }
  const rgbMatch = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
  if (rgbMatch) {
    const r = Math.round(Number(rgbMatch[1]) * factor)
    const g = Math.round(Number(rgbMatch[2]) * factor)
    const b = Math.round(Number(rgbMatch[3]) * factor)
    return `rgb(${r}, ${g}, ${b})`
  }
  return `color-mix(in srgb, ${color} 75%, black)`
}

/** Fundo em gradiente para cards de classificação (mesmo padrão dos KPIs comerciais). */
export function getClassificationCardStyle(color: string | null | undefined): CSSProperties | undefined {
  if (!color?.trim()) return undefined
  const c = color.trim()
  if (c.includes('gradient')) return { background: c }

  let baseColor: string
  if (c.startsWith('rgb') || c.startsWith('rgba') || c.startsWith('#')) {
    baseColor = c
  } else if (/^[0-9a-fA-F]{3,8}$/.test(c)) {
    baseColor = `#${c}`
  } else {
    baseColor = c
  }

  return {
    background: `linear-gradient(135deg, ${baseColor} 0%, ${darkenCssColor(baseColor)} 100%)`,
  }
}

export function getClassificationCardStyleFromMap(
  classification: string | null | undefined,
  colorMap: Map<string, string | null>
): CSSProperties | undefined {
  const key = normalizeClassificationKey(classification)
  return key ? getClassificationCardStyle(colorMap.get(key)) : undefined
}

export function buildClassificationRankMap(
  classifications: Array<{ name: string; min_value: number }>
): Map<string, number> {
  const map = new Map<string, number>()
  for (const row of classifications) {
    const key = normalizeClassificationKey(row.name)
    if (!key) continue
    map.set(key, row.min_value)
  }
  return map
}

export function resolveClassificationTrend(
  current: string | null | undefined,
  previous: string | null | undefined,
  rankMap: Map<string, number>
): ClassificationTrend | null {
  const prevKey = normalizeClassificationKey(previous)
  if (!prevKey) return null

  const currentKey = normalizeClassificationKey(current)
  if (!currentKey) return null

  const currentRank = rankMap.get(currentKey)
  const previousRank = rankMap.get(prevKey)
  if (currentRank === undefined || previousRank === undefined) return null

  if (currentRank > previousRank) return 'upgrade'
  if (currentRank < previousRank) return 'downgrade'
  return 'same'
}

export function classificationTrendLabel(
  trend: ClassificationTrend,
  current: string | null | undefined,
  previous: string | null | undefined
): string {
  const cur = (current || '').trim() || '—'
  const prev = (previous || '').trim() || '—'
  if (trend === 'upgrade') return `Upgrade de ${prev} para ${cur}`
  if (trend === 'downgrade') return `Downgrade de ${prev} para ${cur}`
  return `Manteve a categoria ${cur}`
}

/** Gap máximo (R$) para destacar corretor como “próximo de upgrade” (ex.: Gestão). */
export const CLASSIFICATION_UPGRADE_NEAR_GAP = 500

/** Faixas de filtro da aba Próx. de Upgrade (gap menor ou igual ao valor). */
export const CLASSIFICATION_UPGRADE_GAP_FILTERS = [200, 500, 1000] as const

/** Maior faixa da aba Upgrade — corretores listados até este gap. */
export const CLASSIFICATION_UPGRADE_LIST_MAX_GAP =
  CLASSIFICATION_UPGRADE_GAP_FILTERS[CLASSIFICATION_UPGRADE_GAP_FILTERS.length - 1]

export interface ClassificationProgress {
  monthly_paid_premium: number
  gap_to_next_classification: number | null
  next_classification_name: string | null
  next_min_value: number | null
  is_near_upgrade: boolean
}

/**
 * Progresso até a próxima faixa (mesma lógica de get_dashcorretor_broker_classification_progress).
 * Próxima = menor min_value estritamente maior que o prêmio pago mensal.
 */
export function resolveClassificationProgress(
  monthlyPaidPremium: number | null | undefined,
  classifications: Array<{ name: string; min_value: number }>
): ClassificationProgress {
  const premium =
    typeof monthlyPaidPremium === 'number' && Number.isFinite(monthlyPaidPremium)
      ? monthlyPaidPremium
      : 0

  const sorted = [...classifications]
    .map((c) => ({
      name: (c.name || '').trim(),
      min_value:
        typeof c.min_value === 'number' && Number.isFinite(c.min_value) ? c.min_value : Number(c.min_value),
    }))
    .filter((c) => c.name && Number.isFinite(c.min_value))
    .sort((a, b) => a.min_value - b.min_value || a.name.localeCompare(b.name, 'pt-BR'))

  const next = sorted.find((c) => c.min_value > premium) ?? null
  const gap = next != null ? next.min_value - premium : null

  return {
    monthly_paid_premium: premium,
    gap_to_next_classification: gap,
    next_classification_name: next?.name ?? null,
    next_min_value: next?.min_value ?? null,
    is_near_upgrade: gap != null && gap <= CLASSIFICATION_UPGRADE_NEAR_GAP,
  }
}

export function formatClassificationUpgradeHint(
  progress: ClassificationProgress,
  formatCurrency: (value: number) => string
): string {
  if (progress.next_classification_name == null || progress.gap_to_next_classification == null) {
    return 'Categoria máxima'
  }
  return `Falta ${formatCurrency(progress.gap_to_next_classification)} para ${progress.next_classification_name}`
}
